/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionAbort';

const ActionAbortManager: ActionManagerFn<'ActionAbort'> = (
  platform: Platform,
): ActionManager<'ActionAbort'> => {
  return {
    ...baseActionManager(platform, 'ActionAbort'),
    defaultValue,
    props: {
      ...baseActionProps,
      message: {
        label: 'Message',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
    },
    name: 'Abort Remaining Actions',
    getDisplayText: (item) => item.props.message.value,
    description: `**Overview**<br/>
The Abort Remaining Actions component is a flow control mechanism designed to immediately halt the execution of an action sequence. Its primary function is to conditionally stop processing subsequent actions, effectively acting as a "hard stop" in the logic chain.<br/><br/>
Beyond stopping execution, this component also provides user feedback by requiring a configured message that is displayed as an Error when the abort triggers, clarifying why the process was terminated.

**Key Features**<br/>
* **Execution Halt:** Explicitly aborts the processing of further actions, preventing downstream logic (such as saving, navigation, or additional queries) from running.
* **Mandatory Feedback:** Enforces a user-facing notification by making the message property required.
* **Dynamic Messaging:** Uses an expression-based message field (returning a string), allowing error text to be constructed from runtime variables/conditions.
* **Registry Visibility:** Displays the configured message in the editor (via getDisplayText), making the action's intent readable directly in the action list.

**Properties**<br/>
* **Message (Required):** An expression defining the text to be displayed.
  * *Type:* Expression returning a string.
  * *Behavior:* Displayed to the user as an Error message when the abort triggers.

**Use Cases**<br/>
* **Pre-Save Validation:** Place before a Save to prevent committing data if a business rule or validation condition fails.
* **Conditional Logic Enforcement:** Stop a workflow when prerequisite data is missing or invalid.`,
  };
};

export default ActionAbortManager;
